import React, { useEffect, useState } from 'react';
import { Modal, Select, Button, ConfigProvider } from 'antd';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../store/store';
import { getTemplates } from '../../requests/templates';
import { getPrimaryDark } from '../../constants/constants';

const { Option } = Select;

const RecursiveModelSelectionModal = () => {
  const { t } = useTranslation();
  const pendingChoices = useStore((s) => s.pendingRecursiveModelChoices);
  const injectRecursiveModel = useStore((s) => s.injectRecursiveModel);
  const boxes = useStore((s) => s.boxes);
  const currentDocument = useStore((s) => s.currentDocument);

  const [templates, setTemplates] = useState([]);
  const [selections, setSelections] = useState({});
  // La file n'avance qu'après l'injection : la modale reste montée pendant ce
  // temps. Sans ce verrou, un double-clic relançait injectRecursiveModel sur le
  // même placeholder avant sa suppression → contenu injecté deux fois.
  const [busy, setBusy] = useState(false);

  // On traite la tête de file ; les entrées suivantes s'affichent une fois
  // celle-ci résolue.
  const current = pendingChoices[0] ?? null;
  const open = current != null;

  useEffect(() => {
    if (!current) return;
    getTemplates().then((res) => {
      if (res) setTemplates(res);
    });
    // Sélections repartant de zéro à chaque placeholder dépilé. `current` (tête
    // de file) garde son identité tant que la file n'avance pas : l'effet ne
    // rejoue donc qu'au dépilage, pas à chaque empilement.
    setSelections({});
  }, [current]);

  if (!current) return null;

  const { boxId, missingWorks, forcedTemplates } = current;
  const box = boxes.find((b) => b.id === boxId);
  // Cette modale est montée à la racine de proposalApp, hors du ConfigProvider
  // de Layout.jsx qui teinte les composants antd de la couleur du logo : sans
  // ça, le bouton "Confirmer" retombe sur le bleu par défaut d'antd.
  const accentColor = getPrimaryDark(currentDocument.type === 'template');

  const opportunityTemplates = templates.filter(
    (tpl) => tpl.templatable === 'opportunity' && tpl.pattern === 'template'
  );

  // Dépile la tête de file APRÈS injection : l'injection reste séquentielle
  // (pas de mutation concurrente des boxes) et la modale n'avance à l'entrée
  // suivante qu'une fois celle-ci traitée.
  const dequeue = () => {
    useStore.setState((s) => ({
      pendingRecursiveModelChoices: s.pendingRecursiveModelChoices.slice(1),
    }));
  };

  const resolveWith = async (templatesByWork) => {
    if (busy) return;
    setBusy(true);
    try {
      if (box) await injectRecursiveModel({ box, templatesByWork });
      dequeue();
    } finally {
      setBusy(false);
    }
  };

  const handleConfirm = () => resolveWith({ ...forcedTemplates, ...selections });
  const handleCancel = () => resolveWith(forcedTemplates);

  return (
    <ConfigProvider theme={{ token: { colorPrimary: accentColor } }}>
      <Modal
        open={open}
        onCancel={handleCancel}
        title={t('recursive_model_config_title')}
        footer={[
          <Button key="cancel" onClick={handleCancel} disabled={busy}>{t('cancel')}</Button>,
          <Button key="ok" type="primary" onClick={handleConfirm} loading={busy}>{t('confirm')}</Button>,
        ]}
      >
        <div style={{
          background: '#f0f5ff',
          border: '1px solid #adc6ff',
          borderRadius: 6,
          padding: '10px 14px',
          marginBottom: 20,
          fontSize: 13,
          color: '#1d39c4',
          lineHeight: 1.6,
        }}>
          {t('recursive_model_selection_help')}
        </div>
        {missingWorks.map((work) => {
          const workTemplates = opportunityTemplates.filter((tpl) => tpl.work === work);
          return (
            <div key={work} style={{ marginBottom: 12 }}>
              <div style={{ marginBottom: 4, fontWeight: 500 }}>{t(work)}</div>
              <Select
                style={{ width: '100%' }}
                value={selections[work] ?? null}
                onChange={(val) => setSelections((prev) => ({ ...prev, [work]: val }))}
                allowClear
                // Une agence peut avoir des dizaines de modèles par métier :
                // sans recherche, il ne reste que le défilement.
                showSearch
                optionFilterProp='children'
                filterOption={(input, option) =>
                  (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                }
                placeholder={t('recursive_model_ask_on_creation')}
              >
                {workTemplates.map((tpl) => (
                  <Option key={tpl.id} value={tpl.id}>{tpl.name}</Option>
                ))}
              </Select>
            </div>
          );
        })}
      </Modal>
    </ConfigProvider>
  );
};

export default RecursiveModelSelectionModal;
